/**
 * DocumentVersionHistory - Modal component for viewing document version history
 *
 * Shows all versions of a document in descending order (newest first), as
 * Documents list rows. Each version can be viewed or downloaded, and any set
 * of them selected to compare, comment on, sign or download together.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, Eye, Clock, AlertCircle, DraftingCompass } from "lucide-react";
import { canPreviewFile } from "@/utils/previewUtils";
import { cn } from "@/lib/utils";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { DocumentRowMeta } from "./atoms/DocumentRowMeta";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useDrawingViewer, isDwgParentDoc } from "@/domains/documents/hooks/useDrawingViewer";
import { comparableKind } from "@/domains/documents/utils/versionCompare";
import { useSigningAccess } from "@/domains/signing/useSigningAccess";
import { useDocumentCommentsPanel } from "@/domains/documents/comments/documentCommentsPanelContext";
import type { Document } from "@/types/documents";
import { PdfCompareDialog } from "./PdfCompareDialog";
import { SignDocumentsDialog } from "./organisms/SignDocumentsDialog";
import { DocumentRowContextMenu } from "./organisms/DocumentRowContextMenu";
import { BulkActionBar } from "./molecules/BulkActionBar";
import { IfcDiffOverviewDialog } from "@/domains/buildings/components/ifc-viewer/diff/IfcDiffOverviewDialog";
import {
  getVersionHistory,
  type DocumentVersion,
} from "@/domains/documents/services/versionService";

/** Props for the IfcDiffOverviewDialog, built from a chosen version pair. */
interface VersionComparePair {
  baseDocId: number;
  targetDocId: number;
  baseFilename: string;
  baseVersion: number | null;
  targetFilename: string;
  targetVersion: number | null;
  buildingId: number;
}

interface DocumentVersionHistoryProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** Version group UUID to fetch history for */
  versionGroup: string | null;
  /** Document filename for display */
  filename: string;
  /** Callback when preview is requested. Passes the version's document ID,
   *  storage file_path, and the version's logical filename so the viewer can
   *  label the preview header with the user-facing name (not the UUID). */
  onPreview?: (documentId: string, filePath: string, filename: string) => void;
}

export function DocumentVersionHistory({
  open,
  onOpenChange,
  versionGroup,
  filename,
  onPreview,
}: DocumentVersionHistoryProps) {
  const { t } = useLanguage();
  // Drawings gates on VIEW_DRAWING_COMPARE_TOOL (granted to all three org
  // roles); IFC diff is ungated.
  const { canViewDrawingCompareTool } = usePermissions();
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // Selected versions, in click order. Any number can be picked; each action
  // says how many it takes (compare: two, comments: one). This is the timeline
  // view's only surface for older versions (the list view expands them inline).
  // `comparePair` drives the IFC diff dialog stacked on top.
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [pdfPair, setPdfPair] = useState<{ a: Document; b: Document } | null>(null);
  // Captured when Sign is pressed: closing this modal clears `versions`.
  const [signRequest, setSignRequest] = useState<{
    buildingId: number;
    documents: { id: number; filename: string; size: number }[];
  } | null>(null);
  const signingEnabled = useSigningAccess();
  const commentsPanel = useDocumentCommentsPanel();
  const [comparePair, setComparePair] = useState<VersionComparePair | null>(null);

  // A DWG version row opens that EXACT revision in the Drawings tool — the tool
  // seeds its pickers from the document id, which is the version's own row.
  const drawingBuildingId = versions.find((v) => v.building_id != null)?.building_id;
  const { canUseDrawingViewer, openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId: drawingBuildingId == null ? undefined : String(drawingBuildingId),
  });

  // Gate per ROW, not on the group's anchor filename: a version group is not
  // extension-locked (a .pdf revision can follow a .dwg and vice versa), so the
  // anchor's extension would both hide the action on a genuine DWG revision and
  // offer it on a PDF one that has no geometry to show.
  const canViewDrawingVersion = (v: DocumentVersion) => canUseDrawingViewer && isDwgParentDoc(v);

  const toggleSelected = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (!checked) return prev.filter((x) => x !== id);
      return prev.includes(id) ? prev : [...prev, id];
    });
  };
  const selectedVersions = versions.filter((v) => selectedIds.includes(v.id));
  const allSelected = versions.length > 0 && selectedVersions.length === versions.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : versions.map((v) => v.id));

  // Compare takes exactly two versions of one kind: processed IFCs (diff
  // overview), DWGs (the Drawings tool) or PDFs (side by side). Which files
  // count as comparable models or drawings is versionCompare.ts's rule.
  const pairKindOf = (targets: DocumentVersion[]) => {
    if (targets.length !== 2) return null;
    const kinds = targets.map((v) => comparableKind(v, { drawingsEnabled: canUseDrawingViewer }));
    if (kinds[0] && kinds[0] === kinds[1]) return kinds[0];
    if (targets.every((v) => /\.pdf$/i.test(v.filename))) return "pdf" as const;
    return null;
  };

  const asDocument = (v: DocumentVersion): Document => ({
    id: String(v.id),
    filename: v.filename,
    file_path: v.file_path,
    building_id: String(v.building_id ?? ""),
    content_type: "application/pdf",
    size: v.size,
    created_at: v.created_at,
    version: v.version,
    created_by_name: v.created_by_name ?? undefined,
  });

  const handleCompare = (targets: DocumentVersion[]) => {
    const pairKind = pairKindOf(targets);
    if (!pairKind) return;
    if (pairKind === "pdf") {
      const [a, b] = [...targets].sort((x, y) => x.version - y.version);
      setPdfPair({ a: asDocument(a), b: asDocument(b) });
      onOpenChange(false);
      return;
    }
    const chosen = targets;
    // Order base (older) → target (newer) by version.
    const [a, b] = chosen;
    const { base, target } =
      a.version <= b.version ? { base: a, target: b } : { base: b, target: a };

    // DWG pairs open in the Drawings tool, which prepares their geometry and
    // renders the overlay itself; only IFC has an in-app diff dialog.
    if (pairKind === "dwg") {
      openDrawingViewer([base.id, target.id]);
      onOpenChange(false);
      return;
    }

    const buildingId = base.building_id ?? target.building_id;
    if (buildingId == null) return;
    setComparePair({
      baseDocId: base.id,
      targetDocId: target.id,
      baseFilename: base.filename,
      baseVersion: base.version,
      targetFilename: target.filename,
      targetVersion: target.version,
      buildingId,
    });
    // Hand off to the (near-fullscreen) diff dialog — close the history modal so
    // only one dialog is open. The component stays mounted (callers render it
    // unconditionally), so `comparePair` still drives the diff dialog below.
    onOpenChange(false);
  };

  // Fetch version history when modal opens
  useEffect(() => {
    if (open && versionGroup) {
      setLoading(true);
      setError(null);

      getVersionHistory(versionGroup)
        .then((data) => {
          setVersions(data);
        })
        .catch((err) => {
          console.error("[DocumentVersionHistory] Error fetching versions:", err);
          setError(t.documents.versioning.history.loadError);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, versionGroup, t]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setVersions([]);
      setError(null);
      setSelectedIds([]);
    }
  }, [open]);

  // The dialogs an action hands off to are stacked here, so the history modal
  // closes first — one dialog at a time, as the compare hand-off already does.
  const handleComments = (version: DocumentVersion) => {
    if (!commentsPanel || version.building_id == null) return;
    commentsPanel.open({
      documentId: version.id,
      buildingId: version.building_id,
      filename: version.filename,
    });
    onOpenChange(false);
  };

  const handleSign = (targets: DocumentVersion[]) => {
    const buildingId = targets.find((v) => v.building_id != null)?.building_id;
    if (targets.length === 0 || buildingId == null) return;
    setSignRequest({
      buildingId,
      documents: targets.map((v) => ({ id: v.id, filename: v.filename, size: v.size })),
    });
    onOpenChange(false);
  };

  const handleDownloadAll = async (targets: DocumentVersion[]) => {
    // One at a time: browsers drop parallel programmatic downloads.
    for (const version of targets) await handleDownload(version);
  };

  /**
   * The Documents list's actions for a set of versions — the same handlers
   * feed the floating toolbar (the ticked versions) and the right-click menu
   * (the clicked row, or the selection it belongs to). An undefined handler
   * hides the action, as it does there.
   */
  const actionsFor = (targets: DocumentVersion[]) => {
    const pairKind = pairKindOf(targets);
    const single = targets.length === 1 ? targets[0] : null;
    const compare = () => handleCompare(targets);
    return {
      onDownload: () => void handleDownloadAll(targets),
      onSignature: signingEnabled ? () => handleSign(targets) : undefined,
      canCompare: pairKind === "pdf",
      onCompare: pairKind === "pdf" ? compare : undefined,
      canCompareModels: pairKind === "ifc",
      onDiffOverview: pairKind === "ifc" ? compare : undefined,
      canCompareDrawings: pairKind === "dwg",
      onCompareDrawings: pairKind === "dwg" ? compare : undefined,
      onComments: single && commentsPanel ? () => handleComments(single) : undefined,
      onViewDrawing:
        single && canViewDrawingVersion(single) ? () => openDrawingViewer([single.id]) : undefined,
    };
  };

  /**
   * Download a specific version
   */
  const handleDownload = async (version: DocumentVersion) => {
    try {
      setDownloadingId(version.id);

      const { data, error: downloadError } = await supabase.storage
        .from("documents")
        .download(version.file_path);

      if (downloadError) throw downloadError;

      // Create download link
      const blob = new Blob([data], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Include version in filename for clarity
      // Handle edge cases: no extension, trailing dot, hidden files (.gitignore)
      const lastDotIndex = version.filename.lastIndexOf(".");
      const hasExtension = lastDotIndex > 0 && lastDotIndex < version.filename.length - 1;
      const baseName = hasExtension ? version.filename.slice(0, lastDotIndex) : version.filename;
      const ext = hasExtension ? version.filename.slice(lastDotIndex + 1) : "";
      a.download = ext
        ? `${baseName}_v${version.version}.${ext}`
        : `${baseName}_v${version.version}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Delay URL revocation to ensure download starts successfully
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error("[DocumentVersionHistory] Download error:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  /**
   * Preview a specific version
   */
  const handlePreview = (version: DocumentVersion) => {
    if (onPreview) {
      onPreview(String(version.id), version.file_path, version.filename);
    }
  };

  /**
   * Check if a version can be previewed
   */
  const canPreview = (version: DocumentVersion): boolean => {
    return !isDwgParentDoc(version) && canPreviewFile(version.filename);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t.documents.versioning.history.modalTitle}
            </DialogTitle>
            <DialogDescription className="truncate">{filename}</DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 border-b px-2 py-2.5">
                    <Skeleton className="h-10 w-10 rounded" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            ) : versions.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                {t.documents.versioning.history.empty}
              </div>
            ) : (
              // The same row the Documents list draws: file icon, name, the
              // size · uploaded · uploader line, the version pill, actions.
              <div data-version-history-list>
                <div className="flex items-center gap-3 border-b px-2 pb-2">
                  <Checkbox
                    className="shrink-0"
                    checked={allSelected ? true : selectedIds.length > 0 ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label={t.documents.versioning.history.selectAll}
                  />
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {selectedIds.length > 0
                      ? t.documents.versioning.history.selectedCount.replace(
                          "{count}",
                          String(selectedIds.length)
                        )
                      : t.documents.versioning.history.versionsCount.replace(
                          "{count}",
                          String(versions.length)
                        )}
                  </span>
                </div>
                <div className="divide-y border-b">
                  {versions.map((version, index) => {
                    const isChecked = selectedIds.includes(version.id);
                    // Right-clicking a ticked row acts on the whole selection;
                    // any other row, on itself — as in the Documents list.
                    const menuTargets =
                      isChecked && selectedVersions.length > 1 ? selectedVersions : [version];
                    // The menu has no Signature item; the toolbar offers it.
                    const { onSignature: _signature, ...menuActions } = actionsFor(menuTargets);
                    // The elected head, not the highest number: deleting the
                    // current version elects its predecessor. Rows read without
                    // the flag fall back to the newest-first order.
                    const isCurrent = version.is_latest_version ?? index === 0;
                    return (
                      <DocumentRowContextMenu
                        key={version.id}
                        variant="document"
                        isMulti={menuTargets.length > 1}
                        selectedCount={menuTargets.length}
                        filename={menuTargets.length === 1 ? version.filename : undefined}
                        isDwg={isDwgParentDoc(version)}
                        {...menuActions}
                      >
                        <div
                          className={cn(
                            "flex items-center gap-3 px-2 py-2.5 transition-colors hover:bg-[#f7f7f7]",
                            isCurrent && "bg-primary/5 hover:bg-primary/10",
                            isChecked && "bg-primary/5"
                          )}
                        >
                          <Checkbox
                            className="shrink-0"
                            checked={isChecked}
                            onCheckedChange={(c) => toggleSelected(version.id, c === true)}
                            aria-label={`${t.documents.versioning.history.select} v${version.version}`}
                          />
                          <FileTypeIcon filename={version.filename} />
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-2">
                              <span
                                className="truncate text-sm font-medium"
                                title={version.filename}
                              >
                                {version.filename}
                              </span>
                              {isCurrent && (
                                <span className="shrink-0 rounded-full border border-primary/30 bg-white px-2 text-[11px] font-medium leading-5 text-primary">
                                  {t.documents.versioning.history.current}
                                </span>
                              )}
                            </div>
                            <DocumentRowMeta
                              size={version.size}
                              createdAt={version.created_at}
                              createdByName={version.created_by_name}
                            />
                          </div>
                          <VersionBadge version={version.version} hasHistory={false} />

                          {/* Actions */}
                          <div className="flex shrink-0 gap-1">
                            {canViewDrawingVersion(version) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => openDrawingViewer([version.id])}
                                title={t.documents.bulkActions.viewDrawing}
                                aria-label={t.documents.bulkActions.viewDrawing}
                              >
                                <DraftingCompass className="h-4 w-4" />
                              </Button>
                            )}
                            {canPreview(version) && onPreview && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handlePreview(version)}
                                title="Preview"
                                aria-label={`Preview v${version.version}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDownload(version)}
                              disabled={downloadingId === version.id}
                              title="Download"
                              aria-label={`Download v${version.version}`}
                            >
                              <Download
                                className={cn(
                                  "h-4 w-4",
                                  downloadingId === version.id && "animate-pulse"
                                )}
                              />
                            </Button>
                          </div>
                        </div>
                      </DocumentRowContextMenu>
                    );
                  })}
                </div>
              </div>
            )}
          </ScrollArea>

          {/* The Documents list's floating toolbar, for the ticked versions. Its
              `fixed` position resolves against the dialog (a transformed
              ancestor), so it floats at the dialog's foot. */}
          <BulkActionBar
            selectedCount={selectedVersions.length}
            onClearSelection={() => setSelectedIds([])}
            {...actionsFor(selectedVersions)}
          />

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t.documents.versioning.history.close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {pdfPair && (
        <PdfCompareDialog
          open
          onOpenChange={(o) => {
            if (!o) setPdfPair(null);
          }}
          documentA={pdfPair.a}
          documentB={pdfPair.b}
        />
      )}

      {signRequest && (
        <SignDocumentsDialog
          open
          onOpenChange={(o) => {
            if (!o) setSignRequest(null);
          }}
          buildingId={signRequest.buildingId}
          documents={signRequest.documents}
        />
      )}

      {comparePair && (
        <IfcDiffOverviewDialog
          open
          onOpenChange={(o) => {
            if (!o) setComparePair(null);
          }}
          baseDocId={comparePair.baseDocId}
          targetDocId={comparePair.targetDocId}
          baseFilename={comparePair.baseFilename}
          baseVersion={comparePair.baseVersion}
          targetFilename={comparePair.targetFilename}
          targetVersion={comparePair.targetVersion}
          buildingId={comparePair.buildingId}
        />
      )}
    </>
  );
}
